/**
 * Заказы и оплата (docs/PAYMENTS.md, этап 2).
 *
 * Статус заказа и результат проверки — разные вещи. Тестовый заказ всегда
 * `test`, но рядом хранится, прошёл ли он проверку: `test` + `invalid_quantity`
 * значит «тестовая оплата, количество 2 — на боевом её отправили бы на разбор».
 */

export const ORDER_STATUSES = [
  'received',
  'verifying',
  'granted',
  'awaiting_account',
  'needs_review',
  'test',
  'refunded',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  received: 'Обрабатывается',
  verifying: 'Проверяется в Robokassa',
  granted: 'Доступ выдан',
  awaiting_account: 'Ждёт подтверждения почты',
  needs_review: 'На разбор',
  test: 'Тестовый',
  refunded: 'Возврат',
};

export const ORDER_CHECKS = [
  'ok',
  'unparseable',
  'missing_email',
  'multiple_items',
  'invalid_quantity',
  'missing_product_code',
  'unknown_product',
  'inactive_product',
  'amount_mismatch',
  'unsupported_payment_system',
  'light_during_pro',
  'payment_not_confirmed',
  'unknown_checkout',
  'checkout_mismatch',
] as const;
export type OrderCheck = (typeof ORDER_CHECKS)[number];

export const ORDER_CHECK_LABELS: Record<OrderCheck, string> = {
  ok: 'Заказ корректен',
  unparseable: 'Уведомление не разобрано',
  missing_email: 'В заказе нет почты',
  multiple_items: 'В заказе несколько позиций',
  invalid_quantity: 'Количество больше одного',
  missing_product_code: 'У товара нет кода (не из каталога)',
  unknown_product: 'Товар с таким кодом не найден',
  inactive_product: 'Товар выключен',
  amount_mismatch: 'Сумма не совпадает с ценой',
  unsupported_payment_system: 'Неизвестная платёжная система',
  light_during_pro: 'Лайт куплен при действующем Про',
  payment_not_confirmed: 'Robokassa не подтвердила оплату',
  unknown_checkout: 'Оплата без счёта Creativitas',
  checkout_mismatch: 'Уведомление не совпадает со счётом',
};

/**
 * Откуда заказ: уведомление Tilda или прямая оплата из Creativitas
 * (docs/payments/ROBOKASSA_DIRECT.md). Номера счетов у них не пересекаются.
 */
export const ORDER_PROVIDERS = ['tilda', 'robokassa'] as const;
export type OrderProvider = (typeof ORDER_PROVIDERS)[number];

export const ORDER_PROVIDER_LABELS: Record<OrderProvider, string> = {
  tilda: 'Tilda',
  robokassa: 'Прямая оплата',
};

/**
 * Счёт прямой оплаты. `pending` — ждёт оплаты; `paid` — пришло подписанное
 * уведомление, и сумма и Shp_order совпали со счётом; `mismatch` — уведомление
 * по номеру счёта пришло, но с ним не совпало: заказ на разборе, счёт
 * оплаченным не считается.
 */
export const CHECKOUT_STATUSES = ['pending', 'paid', 'mismatch'] as const;
export type CheckoutStatus = (typeof CHECKOUT_STATUSES)[number];

/** Чем подтверждена оплата. Возврат на страницу payment-success доказательством не считается. */
export const ORDER_CONFIRMATIONS = ['tilda_webhook', 'robokassa_result', 'robokassa_api', 'admin_attested'] as const;
export type OrderConfirmation = (typeof ORDER_CONFIRMATIONS)[number];

export const ORDER_CONFIRMATION_LABELS: Record<OrderConfirmation, string> = {
  tilda_webhook: 'Уведомление Tilda после оплаты',
  robokassa_result: 'Уведомление Robokassa (ResultURL), подпись проверена',
  robokassa_api: 'Проверено в Robokassa по API',
  admin_attested: 'Подтверждено администратором вручную',
};

/**
 * Подтверждения, после которых боевой заказ ещё сверяется с Robokassa по API
 * (OpStateExt) и только потом выдаёт доступ.
 */
export const API_VERIFIED_CONFIRMATIONS: readonly OrderConfirmation[] = ['tilda_webhook', 'robokassa_result'];

/** Независимая проверка оплаты через Robokassa (только боевые операции). */
export const ORDER_VERIFY_STATUSES = [
  'not_needed',
  'not_configured',
  'pending',
  'paid',
  'mismatch',
  'failed',
] as const;
export type OrderVerifyStatus = (typeof ORDER_VERIFY_STATUSES)[number];

export const ORDER_VERIFY_LABELS: Record<OrderVerifyStatus, string> = {
  not_needed: 'Не требуется',
  not_configured: 'Проверка в Robokassa не настроена',
  pending: 'Ожидает проверки в Robokassa',
  paid: 'Robokassa подтвердила оплату',
  mismatch: 'Robokassa не подтверждает оплату',
  failed: 'Robokassa не ответила',
};

export const ORDER_EVENT_ACTIONS = [
  'received',
  'duplicate',
  'reprocessed',
  'verifying',
  'granted',
  'awaiting_account',
  'needs_review',
  'test',
  'claimed',
  'linked',
  'resolved_grant',
  'refunded',
  'note',
  'verified',
  'verify_mismatch',
  'verify_failed',
  'email_sent',
  'email_failed',
] as const;
export type OrderEventAction = (typeof ORDER_EVENT_ACTIONS)[number];

export const ORDER_EVENT_LABELS: Record<OrderEventAction, string> = {
  received: 'Получено уведомление',
  duplicate: 'Повторное уведомление — ничего не изменено',
  reprocessed: 'Повторная обработка',
  verifying: 'Проверка оплаты в Robokassa — доступ выдаётся только после подтверждения',
  granted: 'Доступ выдан',
  awaiting_account: 'Ждёт подтверждения почты',
  needs_review: 'Отправлен на разбор',
  test: 'Тестовый заказ: доступ не выдаётся',
  claimed: 'Почта подтверждена — доступ выдан',
  linked: 'Привязан к пользователю',
  resolved_grant: 'Разбор: доступ выдан',
  refunded: 'Возврат',
  note: 'Заметка',
  verified: 'Robokassa подтвердила оплату',
  verify_mismatch: 'Robokassa не подтверждает оплату',
  verify_failed: 'Robokassa не ответила',
  email_sent: 'Письмо отправлено',
  email_failed: 'Письмо не отправлено',
};

/**
 * Что выдаёт товар: тариф на срок, владение игрой или бессрочный доступ к
 * одному материалу — обучению или МАК-сессии (с 27.10.2026).
 */
export const PRODUCT_GRANT_TYPES = ['plan', 'game_ownership', 'content_access'] as const;
export type ProductGrantType = (typeof PRODUCT_GRANT_TYPES)[number];

/** Какие материалы продаются поштучно. Игры продаются владением, колоды — в составе МАК-сессий. */
export const SELLABLE_CONTENT_KINDS = ['training_material', 'spread'] as const;
export type SellableContentKind = (typeof SELLABLE_CONTENT_KINDS)[number];

export const SELLABLE_CONTENT_LABELS: Record<SellableContentKind, string> = {
  training_material: 'Обучение',
  spread: 'МАК-сессия',
};

/** Путь платёжной ссылки товара: `/buy/<код>`. */
export const buyPath = (code: string): string => `/buy/${encodeURIComponent(code)}`;

/** Откуда строка прав: администратор, заказ, переход на Про или миграция. */
export const ENTITLEMENT_SOURCES = ['admin', 'order', 'upgrade', 'migration'] as const;
export type EntitlementSource = (typeof ENTITLEMENT_SOURCES)[number];

/** Сколько дней после смены цены прежняя ещё принимается, по умолчанию. */
export const DEFAULT_PRICE_GRACE_DAYS = 7;

/** Сколько хранятся тестовые (диагностические) заказы — решение 14.10.2026. */
export const TEST_ORDER_RETENTION_DAYS = 30;

/**
 * Пометка рядом с кнопками оформления, пока приём оплат в тестовом режиме
 * (решение 15.10.2026).
 *
 * Кнопки видит и обычный пользователь до запуска продаж — иначе путь
 * покупателя не пройти целиком. Чтобы тестовая покупка не выглядела
 * настоящей, рядом с ней стоит эта пометка.
 */
export const TEST_OFFER_BADGE = 'тестовая оплата';

/** За сколько дней до окончания тарифа напомнить. */
export const EXPIRY_REMINDER_DAYS = 3;

/** «350» → 35000; «350.50» → 35050. Не число — `null`. */
export function rublesToKopecks(value: unknown): number | null {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim().replace(',', '.') : '';
  if (!/^\d+(\.\d{1,6})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  const kopecks = Number(whole) * 100 + Number((fraction + '00').slice(0, 2));
  return Number.isSafeInteger(kopecks) ? kopecks : null;
}

export function formatRubles(kopecks: number): string {
  const rubles = kopecks / 100;
  return `${Number.isInteger(rubles) ? rubles : rubles.toFixed(2)} ₽`;
}

/* -------------------------------------------------------------------------- */
/* Автопродление (docs/payments/RECURRING.md)                                 */
/* -------------------------------------------------------------------------- */

/**
 * `active` — списания идут; `cancelled` — отключено покупателем, администратором
 * или системой; `failed` — отключено после неудачных попыток списания.
 */
export const RECURRING_STATUSES = ['active', 'cancelled', 'failed'] as const;
export type RecurringStatus = (typeof RECURRING_STATUSES)[number];

/**
 * Повторные попытки неудачного списания (оферта, п. 5.6: порядок раскрыт до
 * подключения — он есть в тексте согласия): через сутки после первой неудачи
 * и через трое. Третья неудача отключает автопродление.
 */
export const RECURRING_RETRY_AFTER_DAYS = [1, 3] as const;

/** Оферта: ссылка у галочки автопродления и в сохранённом согласии. */
export const OFFER_TERMS_URL = 'https://creativitas.ru/oferta';

/**
 * Подпись галочки автопродления — дословно, как требует Robokassa для
 * подключения рекуррентных платежей; «условиям оферты» — ссылка на оферту.
 */
export const RECURRING_CONSENT_LABEL = 'Я согласен на автоматические списания согласно условиям оферты';

/**
 * Начало московских суток, на которые приходится момент. Дата списания в
 * согласии, письмах и профиле — московская (оферта, п. 5.2.2: «в дату начала
 * следующего периода»), и списание не должно прийти днём раньше.
 */
export function moscowDayStart(iso: string): number {
  const offset = 3 * 60 * 60 * 1000;
  const day = 24 * 60 * 60 * 1000;
  return Math.floor((Date.parse(iso) + offset) / day) * day - offset;
}

/** За сколько дней до списания напомнить о нём. */
export const RECURRING_REMINDER_DAYS = 3;

/** Автопродление — только у тарифа на фиксированное число дней (оферта, п. 5.1.2: периоды по 30 дней). */
export function recurringEligible(product: { grantType: ProductGrantType; days: number | null; years?: number | null }): boolean {
  return product.grantType === 'plan' && product.days !== null && product.days > 0 && !product.years;
}

const recurringDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow' }).replace(/\s?г\.$/, '');

/**
 * Текст согласия на автопродление (оферта, п. 5.2.1): цена, период, способ
 * списаний, дата следующего платежа, повторные попытки и как отключить. Этот же
 * текст сохраняется со счётом — видно, под чем стояла галочка.
 */
export function recurringConsentText(input: { title: string; priceKopecks: number; days: number; nextChargeAt: string }): string {
  return [
    `Подключаю автопродление «${input.title}»: ${formatRubles(input.priceKopecks)} каждые ${input.days} дн. тем же способом, которым оплачу сейчас.`,
    `Первое списание — сейчас, следующее — ${recurringDate(input.nextChargeAt)}, затем каждые ${input.days} дн. по этой же цене.`,
    `Если списание не пройдёт, повторим попытку через 1 и через 3 дня, после третьей неудачи автопродление отключится.`,
    'Отключить можно в любой момент в профиле на платформе или письмом в поддержку: доступ сохранится до конца оплаченного периода.',
  ].join(' ');
}

/** Записи журнала автопродления — для админки. */
export const RECURRING_EVENT_LABELS: Record<string, string> = {
  activated: 'Подключено',
  reminded: 'Напомнили о списании',
  charge_requested: 'Списание отправлено в Robokassa',
  charged: 'Списано, тариф продлён',
  charge_failed: 'Списание не прошло',
  failed: 'Отключено: оплата не прошла',
  cancelled: 'Отключено',
};
